import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { UntypedFormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  finalize,
  map,
  mergeMap,
  startWith,
  takeUntil,
  tap
} from 'rxjs/operators';
import { AssignItemsTableComponent } from '../../assign-items-table/assign-items-table.component';
import { BookingItemService } from '../../services/booking-item.service';
import { AsyncPipe } from '@angular/common';
@Component({
  selector: 'assign-items-dialog',
  templateUrl: './assign-items-dialog.component.html',
  styleUrls: ['./assign-items-dialog.component.css'],
  providers: [BookingItemService],
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    FlexModule,
    AssignItemsTableComponent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    AsyncPipe
  ]
})
export class AssignItemsDialogComponent implements OnDestroy {
  @Input() booking;

  form: UntypedFormGroup;

  private savingSource = new BehaviorSubject<boolean>(false);
  private formValidSource = new Subject<Observable<boolean>>();

  disabled$ = combineLatest([
    this.savingSource.asObservable(),
    this.formValidSource.asObservable()
  ]).pipe(
    mergeMap(([saving, valid$]) =>
      valid$.pipe(map((valid) => saving || !valid))
    )
  );

  private ngUnsubscribe = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AssignItemsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private bookingItemService: BookingItemService
  ) {
    if ('booking' in data) {
      this.booking = data['booking'];
    }
  }

  setForm(form: UntypedFormGroup) {
    this.form = form;
    this.formValidSource.next(
      form.statusChanges.pipe(
        map((status) => status === 'VALID'),
        startWith(false)
      )
    );
  }

  saveItems() {
    const changed = this.getChangedItems();
    this.bookingItemService
      .bulkUpdate(changed, this.booking.id)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap(() => this.savingSource.next(true)),
        finalize(() => this.savingSource.next(false))
      )
      .subscribe((success) => {
        const ids = this.booking.items.map((item) => item.id);
        changed.forEach((change) => {
          let item = this.booking.items[ids.indexOf(change.id)];
          Object.assign(item, change);
        });
        this.dialogRef.close(this.booking);
      });
  }

  private getChangedItems() {
    return this.booking.items
      .map((bi) => {
        const matching = this.form.value.items.find(
          ({ bookingItem: { id } }) => id === bi.id
        );
        return { bi, matching };
      })
      .filter(({ bi, matching }) => {
        return (
          bi.item_id !== matching.item.id ||
          bi.product_id !== matching.item.product_id
        );
      })
      .map(({ bi, matching }) => {
        return {
          id: bi.id,
          item_id: matching.item.id,
          product_id: matching.item.product_id
        };
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
